/**
 * Channel Partner presentation helpers — pure functions, no React.
 *
 * Mirrored verbatim in apps/provider/src/features/channel-partner/format.ts;
 * keep the two copies in sync.
 */
import { formatCurrency } from '@finmin/utils';
import type {
  ChannelPartnerApplicationStatus,
  ChannelPartnerHubStatus,
  ChannelPartnerNotificationPreset,
  ChannelPartnerService,
  DecimalValue,
} from '@/api/channel-partners';

/** Decimal strings ("1500.00") and numbers alike → number; null when absent or unparseable. */
export function toNumber(value: DecimalValue | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Money saved keeps `0` (a recorded zero) distinct from `null` (nothing
 * recorded) — the guide is explicit that the two must not be conflated.
 */
export function formatMoneySaved(value: DecimalValue | null | undefined): string {
  const n = toNumber(value);
  return n === null ? 'Not recorded' : formatCurrency(n);
}

/**
 * Customer-facing TAT for the "Expected Closure Date" column. It is a
 * duration, never a calendar date — the backend does not compute a deadline.
 * Same precedence as the customer app's `formatTat`: the service's own
 * expected-TAT text wins, then the min–max day range.
 */
export function formatExpectedClosure(
  service: Pick<ChannelPartnerService, 'expected_tat' | 'customer_tat_min' | 'customer_tat_max'>,
): string | null {
  const expected = service.expected_tat?.trim();
  if (expected) return expected;
  const min = toNumber(service.customer_tat_min);
  const max = toNumber(service.customer_tat_max);
  const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
  if (min !== null && max !== null) return min === max ? days(max) : `${min}–${max} days`;
  if (max !== null) return days(max);
  if (min !== null) return days(min);
  return null;
}

export const HUB_STATUS_LABELS: Record<ChannelPartnerHubStatus, string> = {
  pending: 'Pending',
  ongoing: 'Ongoing',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Order of the Services status filter. */
export const HUB_STATUSES: readonly ChannelPartnerHubStatus[] = [
  'pending',
  'ongoing',
  'completed',
  'cancelled',
];

export function isHubStatus(value: unknown): value is ChannelPartnerHubStatus {
  return typeof value === 'string' && (HUB_STATUSES as readonly string[]).includes(value);
}

const SERVICE_STATE_LABELS: Record<string, string> = {
  CREATED: 'Created',
  IN_PROGRESS: 'In progress',
  ON_HOLD: 'On hold',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

/** Actual service-request state → label; unknown states are humanised, not hidden. */
export function serviceStateLabel(state: string | null | undefined): string {
  if (!state) return '—';
  return (
    SERVICE_STATE_LABELS[state] ??
    state
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
  );
}

/**
 * The status a CP sees: the admin-configured Hub group when the state is
 * mapped, otherwise the actual state (an unmapped state has `hub_status: null`).
 */
export function serviceStatusLabel(service: Pick<ChannelPartnerService, 'hub_status' | 'status'>) {
  return service.hub_status ? HUB_STATUS_LABELS[service.hub_status] : serviceStateLabel(service.status);
}

export const APPLICATION_STATUS_LABELS: Record<ChannelPartnerApplicationStatus, string> = {
  draft: 'Invitation',
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Not approved',
};

export const NOTIFICATION_PRESETS: {
  value: ChannelPartnerNotificationPreset;
  label: string;
  description: string;
}[] = [
  {
    value: 'document_pending',
    label: 'Document Pending',
    description: 'Remind the customer that documents or information are still needed.',
  },
  {
    value: 'action_required',
    label: 'Action Required',
    description: 'Let the customer know an action is waiting on their dashboard.',
  },
];

/** Accept only a CP code's shape: `CP-` + 8 letters/digits (see generate_code). */
export function isChannelPartnerCode(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^CP-[A-Z0-9]{8}$/.test(value.trim().toUpperCase());
}

/** Share text for a CP code. The code is entered in the signup form's referral field. */
export function channelPartnerShareText(code: string): string {
  return `Join FinminOS — one system for your taxes, investments and wealth. Use my Channel Partner code ${code} in the referral code field when you sign up.`;
}

/** "Rohan Sharma" → "RS"; phone-number fallbacks → "?". */
export function initials(name: string | null | undefined): string {
  if (!name || name.startsWith('+')) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}
